import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { QRCodeCanvas } from "qrcode.react";
import { Translate, IdentificationCard, QrCode, X, CaretRight, CaretLeft } from "@phosphor-icons/react";

const SEEN_KEY = "utal_demo_seen_v1";

// Three-step first-login product tour. Trilingual (EN/AR/FR — respects the
// active locale from I18nContext). Persists a `utal_demo_seen_v1` flag so it
// only pops on first login; replay by visiting any /app URL with `?tour=1`.
export default function DemoTour() {
    const { t, lang, toggle, nextLangLabel } = useI18n();
    const loc = useLocation();
    const nav = useNavigate();
    const forceOpen = useMemo(() => new URLSearchParams(loc.search).get("tour") === "1", [loc.search]);
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(0);

    useEffect(() => {
        if (forceOpen) { setOpen(true); setStep(0); return; }
        try {
            if (localStorage.getItem(SEEN_KEY) !== "true") { setOpen(true); setStep(0); }
        } catch (_) { /* private mode */ }
    }, [forceOpen]);

    const finish = () => {
        try { localStorage.setItem(SEEN_KEY, "true"); } catch (_) {}
        setOpen(false);
        if (forceOpen) nav(loc.pathname, { replace: true });
    };

    if (!open) return null;

    const steps = [
        {
            key: "lang",
            icon: Translate,
            body: (
                <div className="space-y-4">
                    <p className="text-sm leading-relaxed text-white/85">{t("tour_lang_body")}</p>
                    <div className="flex items-center gap-3 rounded-sm border border-white/10 bg-white/5 p-3">
                        <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">{t("tour_lang_try")}</div>
                        <Button size="sm" variant="secondary" onClick={toggle} className="rounded-sm" data-testid="tour-lang-toggle">
                            <Translate size={14} weight="bold" className="me-1" />{nextLangLabel()}
                        </Button>
                        <div className="mono text-[10px] text-white/40 ms-auto">EN · AR · FR</div>
                    </div>
                </div>
            ),
        },
        {
            key: "sticker",
            icon: IdentificationCard,
            body: (
                <div className="space-y-4">
                    <p className="text-sm leading-relaxed text-white/85">{t("tour_sticker_body")}</p>
                    {/* Miniature sticker mock */}
                    <div className="flex items-stretch rounded-sm overflow-hidden border border-white/15 bg-white text-black" style={{ height: 96 }}>
                        <div className="flex flex-col items-center justify-center px-2 py-1" style={{ background: "#0a7a3a", color: "white", minWidth: 40 }}>
                            <div className="text-[8px] tracking-widest uppercase opacity-80">Valid</div>
                            <div className="mono font-black text-lg leading-none">12/27</div>
                        </div>
                        <div className="flex-1 p-2 relative">
                            <div className="text-[8px] tracking-[0.25em] uppercase font-bold">UTAL MEA</div>
                            <div className="mono font-black text-base leading-none mt-0.5">M-4711-A</div>
                            <div className="text-[8px] mt-1 text-black/70">Valid until <span className="mono">2027-12-31</span></div>
                            <div className="text-[8px] text-black/70">No. <span className="mono">e4a91b02</span></div>
                            <div className="absolute right-1 bottom-1">
                                <QRCodeCanvas value={"demo:UTALMEA:M-4711-A:sig"} size={48} level="M" includeMargin={false} />
                            </div>
                        </div>
                    </div>
                </div>
            ),
        },
        {
            key: "verify",
            icon: QrCode,
            body: (
                <div className="space-y-4">
                    <p className="text-sm leading-relaxed text-white/85">{t("tour_verify_body")}</p>
                    <div className="grid grid-cols-3 gap-3">
                        <div className="rounded-sm border border-emerald-500/40 bg-emerald-500/10 p-3 text-center">
                            <div className="text-[9px] tracking-widest uppercase text-emerald-400 font-bold">{t("valid")}</div>
                            <div className="mono text-[10px] text-white/70 mt-1">signature ok<br />not revoked</div>
                        </div>
                        <div className="rounded-sm border border-amber-500/40 bg-amber-500/10 p-3 text-center">
                            <div className="text-[9px] tracking-widest uppercase text-amber-400 font-bold">{t("warning")}</div>
                            <div className="mono text-[10px] text-white/70 mt-1">expiring soon<br />or region mismatch</div>
                        </div>
                        <div className="rounded-sm border border-red-500/40 bg-red-500/10 p-3 text-center">
                            <div className="text-[9px] tracking-widest uppercase text-red-400 font-bold">{t("invalid")}</div>
                            <div className="mono text-[10px] text-white/70 mt-1">revoked, expired,<br />or forged</div>
                        </div>
                    </div>
                    <div className="text-[10px] mono text-white/40">{t("tour_verify_footer")}</div>
                </div>
            ),
        },
    ];

    const s = steps[step];
    const Icon = s.icon;
    const isLast = step === steps.length - 1;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="demo-tour">
            <div className="relative w-full max-w-lg rounded-sm border border-white/15 bg-[#0f0f10] text-white shadow-2xl overflow-hidden">
                <div className="grain absolute inset-0 opacity-30 pointer-events-none" />
                <button onClick={finish} className="absolute top-3 end-3 z-20 text-white/60 hover:text-white p-1 rounded-sm" data-testid="tour-close" aria-label={t("cancel")}>
                    <X size={16} weight="bold" />
                </button>

                <div className="relative z-10 p-6 sm:p-8">
                    <div className="flex items-center gap-3 mb-1">
                        <div className="rounded-sm border border-primary/40 bg-primary/10 p-1.5">
                            <Icon size={18} weight="duotone" className="text-primary" />
                        </div>
                        <div className="text-[10px] tracking-[0.35em] uppercase text-white/50 font-bold">{t("tour_kicker")} · {step + 1}/{steps.length}</div>
                    </div>
                    <h2 className="text-2xl font-bold tracking-tight mt-1" data-testid={`tour-title-${s.key}`}>
                        {t(`tour_${s.key}_title`)}
                    </h2>
                    <div className="mt-5" dir={lang === "ar" ? "rtl" : "ltr"}>
                        {s.body}
                    </div>

                    {/* dots */}
                    <div className="flex items-center gap-1.5 mt-6">
                        {steps.map((_, i) => (
                            <div key={i} className={`h-1 rounded-full transition-all ${i === step ? "w-8 bg-primary" : "w-4 bg-white/20"}`} />
                        ))}
                    </div>

                    <div className="flex items-center justify-between gap-3 mt-6">
                        <Button variant="ghost" size="sm" onClick={finish} className="text-white/60 hover:text-white hover:bg-white/5 rounded-sm" data-testid="tour-skip">
                            {t("tour_skip")}
                        </Button>
                        <div className="flex items-center gap-2">
                            {step > 0 && (
                                <Button variant="secondary" size="sm" onClick={() => setStep((n) => n - 1)} className="rounded-sm" data-testid="tour-back">
                                    <CaretLeft size={14} weight="bold" className="me-1" />{t("back")}
                                </Button>
                            )}
                            {!isLast ? (
                                <Button size="sm" onClick={() => setStep((n) => n + 1)} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="tour-next">
                                    {t("next")}<CaretRight size={14} weight="bold" className="ms-1" />
                                </Button>
                            ) : (
                                <Button size="sm" onClick={finish} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="tour-finish">
                                    {t("tour_finish")}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
